import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DocumentSettings {
  documents_enabled: boolean;
  read_only_mode: boolean;
}

export const useDocumentsAccess = () => {
  const [hasAccess, setHasAccess] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ptaId, setPtaId] = useState<string | null>(null);

  useEffect(() => {
    checkDocumentsAccess();
  }, []);

  const checkDocumentsAccess = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user's PTA membership and check if they're a PTA admin
      const { data: memberData } = await supabase
        .from('members')
        .select('pta_id, is_pta_admin')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!memberData?.pta_id) {
        setLoading(false);
        return;
      }

      setPtaId(memberData.pta_id);

      // Check if documents are enabled for this PTA
      const { data: settings } = await supabase
        .from('pta_document_settings')
        .select('documents_enabled, read_only_mode')
        .eq('pta_id', memberData.pta_id)
        .maybeSingle();

      if (!settings?.documents_enabled) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      setIsReadOnly(settings.read_only_mode);

      // PTA admins always have access when documents are enabled
      if (memberData.is_pta_admin) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Check if user has the documents role
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'documents');

      setHasAccess(roles && roles.length > 0);
    } catch (error) {
      console.error('Error checking documents access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  return {
    hasAccess,
    isReadOnly,
    loading,
    ptaId,
    refetch: checkDocumentsAccess,
  };
};